import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  claimAppliance,
  createProvisioningToken,
  fetchAppliances,
  fetchSites,
  LiveClient,
  liveUrl,
  setupCommands,
  type Appliance,
  type LiveState,
  type ProvisioningToken,
  type Site,
} from './fleetApi';
import { EmptyState } from './shared/EmptyState';
import { FormDialog } from './shared/FormDialog';
import { PageHeader } from './shared/PageHeader';
import { linkHandler, routePath } from './router';

interface FleetProps {
  apiUrl: string;
  tenantId: string;
  token: string;
  canClaim: boolean;
  /** May set up new appliances with a provisioning token (appliance.provision). */
  canProvision?: boolean;
  /** Tests inject a fake WebSocket. */
  socketFactory?: (url: string) => WebSocket;
}

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; sites: Site[]; appliances: Appliance[] }
  | { kind: 'error'; error: string };

/** Sites and their appliances, with presence pushed live (UI-001, P2-7). */
export function Fleet({
  apiUrl,
  tenantId,
  token,
  canClaim,
  canProvision = false,
  socketFactory,
}: FleetProps) {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [live, setLive] = useState<LiveState>('connecting');
  const [claimOpen, setClaimOpen] = useState(false);
  const [setup, setSetup] = useState<ProvisioningToken | null>(null);
  const tokenRef = useRef(token);
  const clientRef = useRef<LiveClient | undefined>(undefined);

  const reload = useCallback(
    (signal?: AbortSignal) => {
      const t = tokenRef.current;
      Promise.all([
        fetchSites(apiUrl, t, tenantId, signal),
        fetchAppliances(apiUrl, t, tenantId, signal),
      ])
        .then(([sites, appliances]) => {
          setLoad({ kind: 'ready', sites, appliances });
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setLoad({ kind: 'error', error: err instanceof Error ? err.message : String(err) });
        });
    },
    [apiUrl, tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    reload(controller.signal);
    return () => {
      controller.abort();
    };
  }, [reload]);

  // One live socket per tenant; presence events patch the list in place.
  useEffect(() => {
    const client = new LiveClient({
      url: liveUrl(apiUrl),
      tenantId,
      getToken: () => tokenRef.current,
      socketFactory,
      onState: setLive,
      onResync: () => {
        reload();
      },
      onAppliance: (a) => {
        setLoad((prev) => {
          if (prev.kind !== 'ready') return prev;
          const exists = prev.appliances.some((x) => x.id === a.id);
          const appliances = exists
            ? prev.appliances.map((x) => (x.id === a.id ? { ...x, ...a } : x))
            : [...prev.appliances, a];
          return { ...prev, appliances };
        });
      },
    });
    clientRef.current = client;
    return () => {
      client.close();
    };
  }, [apiUrl, tenantId, reload, socketFactory]);

  // Renewed tokens re-authenticate the open socket.
  useEffect(() => {
    tokenRef.current = token;
    clientRef.current?.refreshToken();
  }, [token]);

  if (load.kind === 'loading') {
    return <Typography color="text.secondary">Loading sites…</Typography>;
  }
  if (load.kind === 'error') {
    return <Alert severity="error">Could not load sites: {load.error}</Alert>;
  }

  return (
    <Stack spacing={2} data-testid="fleet">
      <PageHeader
        title="Sites"
        subtitle="Every site in this tenant, with its appliances' live status."
        actions={
          <>
            <Chip
              size="small"
              data-testid="live-state"
              color={live === 'live' ? 'success' : 'default'}
              variant={live === 'live' ? 'filled' : 'outlined'}
              label={
                live === 'live' ? 'Live' : live === 'connecting' ? 'Connecting…' : 'Reconnecting…'
              }
            />
            {(canClaim || canProvision) && (
              <Button
                variant="contained"
                data-testid="claim-open"
                onClick={() => {
                  setClaimOpen(true);
                }}
              >
                Add appliance
              </Button>
            )}
          </>
        }
      />

      {load.sites.length === 0 && (
        <EmptyState title="No sites yet" testId="sites-empty">
          Sites are where your appliances and lighting live. A tenant admin creates them.
        </EmptyState>
      )}
      {load.sites.map((site) => (
        <SiteCard
          key={site.id}
          site={site}
          appliances={load.appliances.filter((a) => a.site_id === site.id)}
        />
      ))}

      {(canClaim || canProvision) && (
        <AddApplianceDialog
          open={claimOpen}
          sites={load.sites}
          canClaim={canClaim}
          canProvision={canProvision}
          onClose={() => {
            setClaimOpen(false);
          }}
          onClaim={async (claim) => {
            const a = await claimAppliance(apiUrl, tokenRef.current, tenantId, claim);
            setLoad((prev) =>
              prev.kind === 'ready'
                ? { ...prev, appliances: [...prev.appliances.filter((x) => x.id !== a.id), a] }
                : prev,
            );
          }}
          onProvision={async (siteId, unit) => {
            setSetup(
              await createProvisioningToken(apiUrl, tokenRef.current, tenantId, siteId, unit),
            );
          }}
        />
      )}
      <SetupCommandsDialog
        setup={setup}
        onClose={() => {
          setSetup(null); // the token is gone from the page for good
        }}
      />
    </Stack>
  );
}

function SiteCard({ site, appliances }: { site: Site; appliances: Appliance[] }) {
  const path = routePath({ name: 'site', siteId: site.id });
  return (
    <Card variant="outlined" data-testid="site-card">
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
              {site.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {site.timezone}
            </Typography>
          </Box>
          {appliances.length > 0 && (
            <Button
              size="small"
              variant="contained"
              href={path}
              onClick={linkHandler(path)}
              data-testid="open-site"
              sx={{ flexShrink: 0 }}
            >
              Open
            </Button>
          )}
        </Stack>
        {appliances.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No appliances at this site.
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {appliances.map((a) => (
              <ApplianceRow key={a.id} appliance={a} />
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function ApplianceRow({ appliance: a }: { appliance: Appliance }) {
  const online = a.status === 'online';
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={{ xs: 0.5, sm: 2 }}
      data-testid={`appliance-${a.serial}`}
      sx={{
        p: 1.5,
        borderRadius: 1,
        bgcolor: 'action.hover',
        alignItems: { sm: 'center' },
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
          {a.name ?? a.serial}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {a.serial}
          {a.agent_version ? ` · agent ${a.agent_version}` : ''}
          {a.platform === 'sim' ? ' · simulated' : ''}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexShrink: 0 }}>
        <Chip
          size="small"
          data-testid="appliance-status"
          color={online ? 'success' : 'default'}
          label={online ? 'Online' : 'Offline'}
        />
        {!online && a.last_seen_at && (
          <Typography variant="caption" color="text.secondary">
            last seen {new Date(a.last_seen_at).toLocaleString()}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}

type AddMode = 'new' | 'claim';

/**
 * Add appliance: set up a new unit with a one-time token (ADR-0022), or claim
 * one that was provisioned at the factory with the code on its label.
 */
function AddApplianceDialog({
  open,
  sites,
  canClaim,
  canProvision,
  onClose,
  onClaim,
  onProvision,
}: {
  open: boolean;
  sites: Site[];
  canClaim: boolean;
  canProvision: boolean;
  onClose: () => void;
  onClaim: (c: { claim_code: string; site_id: string; name: string }) => Promise<void>;
  onProvision: (siteId: string, unit: { serial: string; name: string }) => Promise<void>;
}) {
  const [chosen, setChosen] = useState<AddMode>('new');
  const mode: AddMode = !canProvision ? 'claim' : !canClaim ? 'new' : chosen;
  const [code, setCode] = useState('');
  const [serial, setSerial] = useState('');
  const [siteId, setSiteId] = useState('');
  const [name, setName] = useState('');
  const site = siteId || sites[0]?.id || '';
  const reset = () => {
    setCode('');
    setSerial('');
    setName('');
  };

  return (
    <FormDialog
      open={open}
      title="Add appliance"
      submitLabel={mode === 'new' ? 'Create setup commands' : 'Add'}
      maxWidth="xs"
      canSubmit={Boolean(name.trim() && site && (mode === 'new' ? serial.trim() : code.trim()))}
      submitTestId={mode === 'new' ? 'provision-submit' : 'claim-submit'}
      testId="add-appliance-dialog"
      onClose={onClose}
      onSubmit={async () => {
        if (mode === 'new') {
          await onProvision(site, { serial: serial.trim(), name: name.trim() });
        } else {
          await onClaim({ claim_code: code, site_id: site, name });
        }
        reset();
        onClose();
      }}
    >
      {canClaim && canProvision && (
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          onChange={(_, v: AddMode | null) => {
            if (v) setChosen(v);
          }}
        >
          <ToggleButton value="new" data-testid="add-mode-new">
            New appliance
          </ToggleButton>
          <ToggleButton value="claim" data-testid="add-mode-claim">
            Claim code
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      <Typography variant="body2" color="text.secondary">
        {mode === 'new'
          ? 'Set up a Debian or Ubuntu machine as an appliance at this site. You get commands to run on it, valid for one hour.'
          : 'Enter the claim code from the label on the appliance.'}
      </Typography>
      {mode === 'new' ? (
        <TextField
          label="Serial number"
          value={serial}
          autoComplete="off"
          helperText="From the unit's label, e.g. IL-0001"
          slotProps={{ htmlInput: { 'data-testid': 'provision-serial', spellCheck: false } }}
          onChange={(e) => {
            setSerial(e.target.value);
          }}
        />
      ) : (
        <TextField
          label="Claim code"
          value={code}
          autoComplete="off"
          slotProps={{ htmlInput: { 'data-testid': 'claim-code' } }}
          onChange={(e) => {
            setCode(e.target.value);
          }}
        />
      )}
      <FormControl>
        <InputLabel id="claim-site-label">Site</InputLabel>
        <Select
          labelId="claim-site-label"
          label="Site"
          value={site}
          onChange={(e) => {
            setSiteId(e.target.value);
          }}
        >
          {sites.map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Name"
        value={name}
        placeholder="e.g. Lobby controller"
        slotProps={{ htmlInput: { 'data-testid': 'claim-name' } }}
        onChange={(e) => {
          setName(e.target.value);
        }}
      />
    </FormDialog>
  );
}

/** The one-time setup commands for a new unit; the token is shown only here. */
function SetupCommandsDialog({
  setup,
  onClose,
}: {
  setup: ProvisioningToken | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const commands = setup ? setupCommands(setup) : '';
  return (
    <Dialog
      open={setup !== null}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') onClose(); // don't lose the token to a stray tap
      }}
      maxWidth="md"
      fullWidth
      data-testid="setup-dialog"
    >
      <DialogTitle>Set up {setup?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">
            Run these on the unit before{' '}
            {setup ? new Date(setup.expires_at).toLocaleTimeString() : ''}. The token in them works
            once and is shown only now.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            On a Debian or Ubuntu machine (WSL works too, with systemd enabled), as a user with
            sudo. It installs the agent, gives the unit its identity, and adds it to{' '}
            {setup?.site_name} as &ldquo;{setup?.name}&rdquo; ({setup?.serial}).
          </Typography>
          <Box
            component="pre"
            data-testid="setup-commands"
            sx={{
              m: 0,
              p: 1.5,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontFamily: 'monospace',
              fontSize: 13,
              overflowX: 'auto',
              whiteSpace: 'pre',
            }}
          >
            {commands}
          </Box>
          <Typography variant="body2" color="text.secondary">
            The appliance appears here as Online within a minute of the last command.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<ContentCopyIcon />}
          data-testid="copy-setup"
          onClick={() => {
            void navigator.clipboard.writeText(commands).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
        <Button
          variant="contained"
          data-testid="setup-done"
          onClick={() => {
            setCopied(false);
            onClose();
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
